/**
 * The native-file-picker half shared by FileInput and AudioInput: clear the
 * input so the same file can be re-picked after a failure, turn every picked
 * file into a FileJSON — uploaded once and named by id when the Widget provides
 * an uploader, read inline otherwise — and keep one error/reading state the
 * widget renders.
 */

import { useCallback, useContext, useRef, useState } from 'preact/hooks'
import { EmbedApiError } from '../../core/api'
import { type FileJSON, FileReadError, fileToFileJSON, formatBytes } from '../../core/file-value'
import { FileUploaderContext } from '../../core/upload'

export function usePickFiles(
  maxSizeMB: number | null | undefined,
  /** The values, and the picked files themselves (for a local preview). */
  onFiles: (values: FileJSON[], picked: File[]) => void,
  /** Subject of the fallback error sentence: "Filen" / "Lydfilen". */
  subject: string
) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [reading, setReading] = useState(false)
  const upload = useContext(FileUploaderContext)

  const handlePick = useCallback(
    async (event: Event) => {
      const input = event.target as HTMLInputElement
      const picked = Array.from(input.files ?? [])
      // Clearing the native input immediately lets the user re-pick the same
      // file after a failure — otherwise `change` never fires a second time.
      input.value = ''
      if (picked.length === 0) return

      setError(null)
      setReading(true)
      try {
        const values = await Promise.all(
          picked.map((file) => {
            if (!upload) return fileToFileJSON(file, maxSizeMB)
            if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
              throw new FileReadError(
                `Filen "${file.name}" fylder ${formatBytes(file.size)} og overstiger grænsen på ${maxSizeMB} MB.`,
                file.name
              )
            }
            return upload(file)
          })
        )
        onFiles(values, picked)
      } catch (err) {
        setError(
          err instanceof FileReadError || err instanceof EmbedApiError
            ? err.message
            : `${subject} kunne ikke læses: ${err instanceof Error ? err.message : 'Ukendt fejl'}`
        )
      } finally {
        setReading(false)
      }
    },
    [maxSizeMB, onFiles, subject, upload]
  )

  return { inputRef, handlePick, reading, error, setError }
}
